import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    useIslandOverride,
    type IslandMode,
} from '~/ui-rocco/pages/detail-shared';

import { BulkEnquireExitConfirmIsland } from './BulkEnquireExitConfirmIsland';
import { BulkEnquireIsland } from './BulkEnquireIsland';
import { BulkEnquiryModal } from './BulkEnquiryModal';
import {
    useBulkEnquiryMode,
    useBulkEnquirySelection,
} from './bulk-enquiry-state';
import { useIsBelowMd } from './use-below-md';

/**
 * Orchestrates the Bulk Enquire flow on the directory browse pages, driving the
 * Magic Island via its override (the scroll-driven island sections don't
 * reliably surface here):
 * - mode off + grid in view → a "Try bulk enquiry" prompt (enters mode),
 * - mode on → a persistent count + submit island ({@link BulkEnquireIsland}),
 *   whose list fragment opens a cart-style preview of the selected items,
 * - otherwise → released, so the island is free for other sections up top.
 *
 * Mounted once inside the browse page's `MagicIslandProvider`. Mode and
 * selection persist across navigation (sessionStorage); the modal reads them
 * live and clears on a successful submit (see {@link BulkEnquiryModal}).
 */
export const BulkEnquire = ({
    gridInView,
    entityType,
}: {
    gridInView: boolean;
    entityType: 'products' | 'professionals' | 'projects';
}) => {
    const { active, enter, exit } = useBulkEnquiryMode();
    const { items, remove, clear } = useBulkEnquirySelection();
    const belowMd = useIsBelowMd();

    const [open, setOpen] = useState(false);
    // Submitting while logged out raises a login prompt and closes this modal to
    // make way for it. If they back out of the login, bring it back where they
    // were — on the form — instead of at the review step.
    const [resumeAtForm, setResumeAtForm] = useState(false);

    const openModal = useCallback(() => {
        setResumeAtForm(false);
        setOpen(true);
    }, []);
    const closeModal = useCallback(() => setOpen(false), []);
    const reopenAfterAuthCancel = useCallback(() => {
        setResumeAtForm(true);
        setOpen(true);
    }, []);

    // Exiting clears the selection, so a non-empty list gets a confirm step
    // before the X on the island throws it away: the island morphs into an
    // inline Discard / Keep pill (same glass surface). An empty list exits
    // directly.
    const [confirmingExit, setConfirmingExit] = useState(false);
    const hasSelection = items.length > 0;
    const requestExit = useCallback(() => {
        if (hasSelection) {
            setConfirmingExit(true);
            return;
        }
        exit();
    }, [hasSelection, exit]);
    const keepSelecting = useCallback(() => setConfirmingExit(false), []);
    const confirmExit = useCallback(() => {
        setConfirmingExit(false);
        exit();
    }, [exit]);

    // The confirm step only makes sense while bulk mode is on. Any path that
    // turns it off without going through Keep/Discard — a successful submit, the
    // post-login auto-submit reset — must not leave the flag set, or the next
    // enter() would surface the discard confirmation instead of the selection
    // island. Re-entering always starts clean.
    useEffect(() => {
        if (!active) setConfirmingExit(false);
    }, [active]);

    const islandMode = useMemo<IslandMode | null>(() => {
        if (active) {
            return {
                kind: 'custom',
                // Shares the CTA's dark-pill surface so entering/exiting bulk
                // mode morphs the glass pill in place (shared layoutId) instead
                // of popping the island out and back in.
                surface: 'pill',
                content: confirmingExit ? (
                    <BulkEnquireExitConfirmIsland
                        items={items}
                        onKeep={keepSelecting}
                        onDiscard={confirmExit}
                    />
                ) : (
                    <BulkEnquireIsland
                        items={items}
                        entityType={entityType}
                        onRemove={remove}
                        onClearAll={clear}
                        onExit={requestExit}
                        onSubmit={openModal}
                    />
                ),
            };
        }
        if (gridInView) {
            return {
                kind: 'cta',
                // The pill sizes to its nowrap label, so the full invitation
                // overflows a 414px viewport — below md it drops the hook and
                // keeps the action.
                label: belowMd
                    ? 'Like this? Start an enquiry list'
                    : "Like what you're seeing? Start an enquiry list",
                onClick: enter,
            };
        }
        return null;
    }, [
        active,
        gridInView,
        items,
        entityType,
        remove,
        clear,
        enter,
        requestExit,
        keepSelecting,
        confirmExit,
        confirmingExit,
        openModal,
        belowMd,
    ]);

    useIslandOverride(islandMode);

    return (
        <BulkEnquiryModal
            open={open}
            startAtForm={resumeAtForm}
            onClose={closeModal}
            onAuthCancel={reopenAfterAuthCancel}
        />
    );
};
